import { range } from 'lodash';
import moment from 'moment';
import React from 'react';
import { Tooltip } from 'antd';
import { dateTimeDisplayFormat, toNoTimeZoneDate } from '@/config';
import { useTranslation } from 'react-i18next';
import { TripStatusType } from '@/constants';
import { TripDetailCalendarDto } from '@/__generated';

type Props = {
  trip: TripDetailCalendarDto;
  index: number;
  date: any;
  showDetail: (trip: TripDetailCalendarDto) => void;
};

export default function TripRow({ trip, index, date, showDetail }: Props) {
  const { t } = useTranslation();
  const { className, startActiveHour, endActiveHour, title, firstTitle, secondTitle, startOffsetPx, endOffsetPx } = React.useMemo(() => {
    let classNames: string[] = [];

    var departureDate = toNoTimeZoneDate(trip.plannedDepartureDateTime);
    var arrivalDate = toNoTimeZoneDate(trip.plannedArrivalDateTime);
    var startActiveHour = departureDate.hour();
    var startActiveMinutes = departureDate.minute();
    var endActiveHour = arrivalDate.hour();
    var endActiveMinutes = arrivalDate.minute();
    var firstTitle = `${departureDate.format('HH:mm')} ${trip.sourceStation}`;
    var secondTitle = `${trip.destinationStation} ${arrivalDate.format('HH:mm')}`;

    if (!moment(toNoTimeZoneDate(trip.plannedDepartureDateTime)).isSame(moment(date), 'D')) {
      classNames.push('start-from-prev-day');
      startActiveHour = 0;
      firstTitle = `${trip.sourceStation}`;
      startActiveMinutes = 0;
    } else {
      startActiveMinutes = startActiveMinutes / 60;
    }
    if (!moment(date).isSame(moment(toNoTimeZoneDate(trip.plannedArrivalDateTime)), 'D')) {
      classNames.push('end-next-day');
      endActiveHour = 24;
      secondTitle = `${trip.destinationStation}`;
      endActiveMinutes = 0;
    } else {
      endActiveMinutes = endActiveMinutes / 60;
    }

    return {
      className: classNames.join(' '),
      startActiveHour: startActiveHour,
      endActiveHour: endActiveHour,
      departureDate: departureDate,
      arrivalDate: arrivalDate,
      secondTitle: secondTitle,
      firstTitle: firstTitle,
      startOffsetPx: Math.round(startActiveMinutes * 50),
      endOffsetPx: Math.round(endActiveMinutes * 50),
      title: (
        <div style={{ fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <b>{trip.tripName}</b>
            <b>{trip.tripRefNumber}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('tripsPage_DepartureDateTime')}</span>
            <b>{dateTimeDisplayFormat.originDateTime(trip.plannedDepartureDateTime)}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('tripsPage_ArrivalDateTime')}</span>
            <b>{dateTimeDisplayFormat.originDateTime(trip.plannedArrivalDateTime)}</b>
          </div>
        </div>
      ),
    };
  }, [trip, date]);

  const colSpan = React.useMemo(() => {
    return range(startActiveHour, endActiveHour).length || 1;
  }, [startActiveHour, endActiveHour]);
  return (
    <tr>
      <td className="no">{index}</td>
      <td className="trip-name">{trip.tripName}</td>
      {startActiveHour > 0
        ? range(0, startActiveHour).map(h => {
            return <td className="hour" key={h}></td>;
          })
        : null}

      <td className="hour active" colSpan={colSpan}>
        <Tooltip title={title} overlayInnerStyle={{ width: 300 }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              width: `calc(100% + ${endOffsetPx - startOffsetPx}px)`,
              left: startOffsetPx,
              backgroundColor: TripStatusType.__getColor(trip.status || 0),
            }}
            className={`trip-range ${className}`}
            onClick={() => {
              showDetail(trip);
            }}
          >
            <span className="trip-info first">{firstTitle}</span>
            <span className="trip-info second">{secondTitle}</span>
          </div>
        </Tooltip>
      </td>
      {range(startActiveHour + colSpan, 24).map(h => {
        return <td className="hour" key={h}></td>;
      })}
    </tr>
  );
}
